/**
 * workoutConsistency.ts
 *
 * Single source of truth for the workout consistency qualification rule.
 *
 * A day counts as a Workout Day only if at least one qualifying workout exists.
 *
 * Rule:
 *   - Run        → always qualifies
 *   - Gym        → always qualifies (weightlifting / gym)
 *   - Other      → always qualifies
 *   - Walk       → qualifies only if durationMinutes >= 30
 *   - Everything else → does not qualify
 *
 * Used by: useConsistencyStrip
 * Must not be used for: calorie calculations, workout logging, steps logic
 */

/** Minimum walk duration (minutes) required to count as a workout day */
export const WALK_CONSISTENCY_THRESHOLD_MINUTES = 30;

export interface WorkoutForConsistency {
  type?: string;
  /** Duration in minutes */
  durationMinutes?: number;
  /** Duration in seconds (alternative field name used by some entries) */
  duration?: number;
}

/**
 * Returns true if this workout qualifies as a completed workout day
 * on the Consistency Strip.
 */
export function qualifiesForWorkoutConsistency(
  workout: WorkoutForConsistency,
): boolean {
  const type = workout.type?.toLowerCase() ?? '';

  if (type === 'run') return true;
  if (type === 'weightlifting' || type === 'gym') return true;
  if (type === 'other') return true;

  if (type === 'walk') {
    // Resolve duration in minutes — prefer durationMinutes, fall back to
    // duration (seconds) divided by 60, fall back to 0.
    const minutes =
      workout.durationMinutes != null
        ? workout.durationMinutes
        : workout.duration != null
        ? workout.duration / 60
        : 0;
    return minutes >= WALK_CONSISTENCY_THRESHOLD_MINUTES;
  }

  return false;
}
